// Telemetry read hooks (api_contracts §5.1) for the instrument dashboard:
// `useTelemetryLatest` seeds first paint + freshness (each sample carries its
// own timestamp); live WS samples from BoatChromeContext layer on top.
// `useTelemetryRange` feeds sparklines and the history chart.

import { useEffect, useMemo, useState } from 'react';

import { api } from '../api';

export interface LatestSampleRow {
  path: string;
  time: string;
  value: unknown;
  unit: string | null;
  source: string | null;
  deviceId: string | null;
}

/**
 * The `?paths=` query for a scoped read, or `''` for the whole boat. The
 * endpoint takes ONE comma-separated list (`owner-routes.ts`), so the paths are
 * joined, not repeated — and sorted, so the same set is one stable effect key
 * however the caller ordered it.
 */
export function latestPathsQuery(paths: readonly string[] | undefined): string {
  if (paths === undefined) return '';
  const unique = [...new Set(paths.filter((p) => p.length > 0))].sort();
  return unique.join(',');
}

/**
 * The newest sample per path. `paths` omitted reads the WHOLE boat (the
 * instrument dashboard's first paint); passing a list scopes the read to it (the
 * alarm detail page's live panel, which wants four paths, not four hundred).
 *
 * An EMPTY list fetches nothing and returns an empty map — a caller that has no
 * paths yet must not accidentally ask for every one of them.
 */
export function useTelemetryLatest(
  boatId: string,
  paths?: readonly string[]
): ReadonlyMap<string, LatestSampleRow> {
  const [latest, setLatest] = useState<ReadonlyMap<string, LatestSampleRow>>(new Map());
  const scoped = paths !== undefined;
  const pathsKey = latestPathsQuery(paths);
  useEffect(() => {
    if (boatId === '' || (scoped && pathsKey === '')) {
      setLatest(new Map());
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await api<{ latest: LatestSampleRow[] }>(
          `/v1/boats/${boatId}/telemetry/latest${pathsKey === '' ? '' : `?paths=${encodeURIComponent(pathsKey)}`}`
        );
        if (!alive) return;
        setLatest(new Map(res.latest.map((r) => [r.path, r])));
      } catch {
        // First paint just waits for live samples; freshness dims handle it.
      }
    })();
    return () => {
      alive = false;
    };
  }, [boatId, scoped, pathsKey]);
  return latest;
}

export interface RangePoint {
  t: string;
  avg: number;
  min: number;
  max: number;
  count: number;
}

export interface RangeSeries {
  unit: string | null;
  points: RangePoint[];
}

export type RangeWindow = '1h' | '24h' | '7d' | '30d';

export const RANGE_WINDOW_MS: Record<RangeWindow, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

export interface RangeState {
  kind: 'loading' | 'ready' | 'error';
  series: Record<string, RangeSeries>;
}

/** What `GET …/telemetry/range` answers (api_contracts §5.1). */
export interface RangeResponse {
  from: string;
  to: string;
  /** The bucket size the server used; a bucket with no samples is left out. */
  bucketSeconds: number;
  series: Record<string, RangeSeries>;
}

/** How often a range on screen is read again. */
export const RANGE_REFRESH_MS = 60_000;

/**
 * A window's end for the page clock `nowMs`: the last millisecond of its
 * minute (RANGE_REFRESH_MS), so a ticking clock reads the same window until
 * the next refresh.
 */
export function rangeWindowEndMs(nowMs: number): number {
  return Math.floor(nowMs / 60_000) * 60_000 + 59_999;
}

/**
 * Bucketed avg/min/max per path over [fromMs, toMs], at most `points`
 * buckets: the read the sparklines, the history chart and a board's history
 * panels share (TOM-591).
 */
export function fetchTelemetryRange(
  boatId: string,
  paths: readonly string[],
  fromMs: number,
  toMs: number,
  points: number
): Promise<RangeResponse> {
  const pathsKey = [...paths].sort().join(',');
  return api<RangeResponse>(
    `/v1/boats/${boatId}/telemetry/range?paths=${encodeURIComponent(pathsKey)}` +
      `&from=${new Date(fromMs).toISOString()}&to=${new Date(toMs).toISOString()}&points=${points}`
  );
}

export function useTelemetryRange(
  boatId: string,
  paths: string[],
  window_: RangeWindow,
  points = 200,
  /**
   * Clock the window's `to` edge anchors at. Pass the page's playback-aware
   * `nowMs` so a replaying boat's charts bracket the RECORDING's now — with
   * the wall clock, historical samples fall outside every window and the
   * charts read empty. Quantized to one minute in the effect key (matching
   * RANGE_REFRESH_MS) so a ticking clock does not refire the fetch.
   */
  anchorNowMs?: number
): RangeState {
  const [state, setState] = useState<RangeState>({ kind: 'loading', series: {} });
  const pathsKey = useMemo(() => [...paths].sort().join(','), [paths]);
  // Anchor quantized to one minute (RANGE_REFRESH_MS): the page's 5 s clock
  // tick must not refire the fetch, and a static variable keeps the effect's
  // dependency array checkable.
  const anchorMinute = anchorNowMs === undefined ? null : Math.floor(anchorNowMs / 60_000);

  useEffect(() => {
    if (pathsKey === '') {
      setState({ kind: 'ready', series: {} });
      return;
    }
    let alive = true;
    async function load() {
      try {
        const to = anchorMinute === null ? Date.now() : rangeWindowEndMs(anchorMinute * 60_000);
        const res = await fetchTelemetryRange(
          boatId,
          pathsKey.split(','),
          to - RANGE_WINDOW_MS[window_],
          to,
          points
        );
        if (!alive) return;
        setState({ kind: 'ready', series: res.series });
      } catch {
        if (!alive) return;
        setState((prev) => ({ kind: 'error', series: prev.series }));
      }
    }
    setState((prev) => ({ kind: 'loading', series: prev.series }));
    void load();
    const id = window.setInterval(load, RANGE_REFRESH_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [boatId, pathsKey, window_, points, anchorMinute]);

  return state;
}
